import { Component, HostListener, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DirectorFormComponent } from '../director-form/director-form.component';
import { HasUnsavedChanges } from '../../../guards/unsaved-changes.guard';
import { DirectorService } from '../../../services/director.service';
import { InfoDialogService } from '../../../services/info-dialog.service';
import { forkJoin, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

interface CreateResult {
  success: boolean;
  directorId?: string;
  photoUploaded?: boolean;
  photoError?: any;
}

@Component({
  selector: 'app-director-create',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DirectorFormComponent],
  templateUrl: './director-create.component.html',
  styleUrls: ['./director-create.component.scss']
})
export class DirectorCreateComponent implements HasUnsavedChanges {
  @ViewChild(DirectorFormComponent) directorFormComponent!: DirectorFormComponent;
  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private directorService: DirectorService,
    private router: Router,
    private infoDialogService: InfoDialogService
  ) {
    this.form = this.fb.group({
      fullName: ['', Validators.required],
      yearOfBirth: ['', [Validators.min(1900), Validators.max(new Date().getFullYear())]],
      yearOfBirthConfirmed: [false],
      age: [''],
      gender: [0],
      honorificTitle: [''],
      nationality: [''],
      secondNationality: [''],
      isDeceased: [false],
      dateOfDeath: [''],
      cvSummary: [''],
      photoFileName: [''],
      profession: [''],
      industryExpertise1: [''],
      industryExpertise2: [''],
      qualification: [''],
      hasFinancialExpertise: [false],
      financialExpertise: [''],
      hasPoliticalPosition: [false],
      politicalPosition: [''],
      isCompleted: [false],
      dateCompleted: [''],
      isActive: [true],
      workExperiences: [[]],
      trainingAndEducations: [[]]
    });
  }

  hasUnsavedChanges(): boolean {
    return this.directorFormComponent?.hasUnsavedChanges() ?? false;
  }

  // Warn on browser tab close / refresh / external navigation. Browsers only allow
  // a native, non-customizable prompt here — the Material confirmation dialog cannot
  // be shown during unload. The in-app canDeactivate guard still handles SPA navigation.
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      // Legacy browsers require returnValue to be set to trigger the prompt.
      event.returnValue = true;
    }
  }

  onSubmit() {
    if (this.form.valid) {
      const formValue = this.form.value;

      // Map form values to CreateDirectorRequest
      // Extract IDs from objects or use direct ID values
      const createRequest = {
        fullName: formValue.fullName,
        yearOfBirth: formValue.yearOfBirth,
        yearOfBirthConfirmed: formValue.yearOfBirthConfirmed,
        honorificTitleId: formValue.honorificTitle?.id || formValue.honorificTitle,
        gender: formValue.gender,
        nationalityId: formValue.nationality?.id || formValue.nationality,
        secondNationalityId: formValue.secondNationality?.id || formValue.secondNationality || null,
        isDeceased: formValue.isDeceased,
        dateOfDeath: formValue.dateOfDeath,
        professionId: formValue.profession?.id || formValue.profession,
        industryExpertise1Id: formValue.industryExpertise1?.id || formValue.industryExpertise1,
        industryExpertise2Id: formValue.industryExpertise2?.id || formValue.industryExpertise2 || null,
        qualificationId: formValue.qualification?.id || formValue.qualification,
        hasFinancialExpertise: formValue.hasFinancialExpertise,
        financialExpertiseId: formValue.financialExpertise?.id || formValue.financialExpertise || null,
        hasPoliticalPosition: formValue.hasPoliticalPosition,
        politicalPositionId: formValue.politicalPosition?.id || formValue.politicalPosition || null,
        cvSummary: formValue.cvSummary,
        workExperience: this.transformWorkExperienceToFlat(formValue.workExperiences || []),
        trainingOrEducation: this.transformTrainingAndEducationToFlat(formValue.trainingAndEducations || [])
      };

      this.directorService.createDirector(createRequest).pipe(
        switchMap((createdDirector) => {
          // Check if there's a photo to upload
          const photoFile = this.directorFormComponent?.getPhotoFromForm();
          if (photoFile && createdDirector.id) {
            // Upload the photo
            return this.directorService.uploadDirectorPhoto(createdDirector.id, photoFile).pipe(
              switchMap(() => of<CreateResult>({ success: true, directorId: createdDirector.id, photoUploaded: true })),
              catchError((error) => {
                console.error('Error uploading photo:', error);
                return of<CreateResult>({ success: true, directorId: createdDirector.id, photoUploaded: false, photoError: error });
              })
            );
          }
          return of<CreateResult>({ success: true, directorId: createdDirector.id, photoUploaded: false });
        }),
        catchError((error) => {
          console.error('Error creating director:', error);
          this.infoDialogService.openErrorDialog(
            'Failed to create director. Please try again.',
            'Creation Error'
          );
          return of<CreateResult>({ success: false });
        })
      ).subscribe({
        next: (result) => {
          if (result.success) {
            // The director is persisted, so navigating away below must not trip
            // the unsaved-changes guard.
            this.form.markAsPristine();
            if (result.photoUploaded) {
              this.infoDialogService.openSuccessDialog(
                'Director and photo have been created successfully!',
                'Success'
              ).subscribe(() => {
                this.router.navigate(['/directors/list']);
              });
            } else if (result.photoError) {
              this.infoDialogService.openWarningDialog(
                'Director was created successfully, but the photo upload failed. You can try uploading the photo again by editing the director.',
                'Partial Success'
              ).subscribe(() => {
                this.router.navigate(['/directors/list']);
              });
            } else {
              this.router.navigate(['/directors/list']);
            }
          }
        }
      });
    }
  }

  private transformWorkExperienceToFlat(workExperiences: any[]): any[] {
    return workExperiences.map(we => {
      // Initialize all company fields as null
      const flatWe: any = {
        id: we.id,
        companyId: null,
        companyName: null,
        opbCompanyId: null,
        opbCompanyName: null,
        workExperienceCompanyId: null,
        workExperienceCompanyName: null,
        auditFirmId: null,
        auditFirmName: null,
        largeShareholderId: null,
        largeShareholderName: null,
        jobPositionId: we.position?.id || null,
        jobPositionName: we.position?.name || null,
        from: we.startDate || null,
        until: we.endDate || null
      };

      // Set the appropriate company fields based on companyType
      if (we.company && we.companyType) {
        switch (we.companyType) {
          case 'opbCompany':
            flatWe.opbCompanyId = we.company.id;
            flatWe.opbCompanyName = we.company.name;
            break;
          case 'company':
            flatWe.companyId = we.company.id;
            flatWe.companyName = we.company.name;
            break;
          case 'workExperienceCompany':
            flatWe.workExperienceCompanyId = we.company.id;
            flatWe.workExperienceCompanyName = we.company.name;
            break;
          case 'auditFirm':
            flatWe.auditFirmId = we.company.id;
            flatWe.auditFirmName = we.company.name;
            break;
          case 'largeShareholder':
            flatWe.largeShareholderId = we.company.id;
            flatWe.largeShareholderName = we.company.name;
            break;
        }
      }

      return flatWe;
    });
  }

  private transformTrainingAndEducationToFlat(trainingAndEducations: any[]): any[] {
    return trainingAndEducations.map(te => ({
      id: te.id,
      educationFieldId: te.educationField?.id || null,
      educationFieldName: te.educationField?.name || null,
      universityId: te.university?.id || null,
      universityName: te.university?.name || null,
      type: te.type
    }));
  }

  onCancel() {
    this.router.navigate(['/directors']);
  }
}
